/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/ClaimEditorHeader.tsx
 * FILE_ID: QUOTE-CLAIMS-G10-EDITOR-HEADER-001
 * PURPOSE: Accessible editor navigation, sync state and capability-driven actions.
 */
import { CheckCircle2, Download, FileText, Save, Send } from 'lucide-react'
import { hasMoneyDocumentAction } from '@quotech/api'
import type { ClaimEditorController } from './useClaimEditorController'

const SAVE_LABELS = {
  saved: 'All changes saved',
  pending: 'Autosave pending',
  saving: 'Saving changes…',
  offline: 'Offline · changes queued',
  error: 'Autosave failed · retry',
  conflict: 'Conflict · reload required',
} as const

export function ClaimEditorHeader({ controller, onBack, onDownload, onSendClient }: {
  controller: ClaimEditorController
  onBack: () => void
  onDownload: (format: 'pdf' | 'docx' | 'xlsx') => void
  onSendClient: () => void
}) {
  const claim = controller.claim!
  const sequence = (claim as { contract_sequence?: number | null })
    .contract_sequence ?? claim.claim_number
  return (
    <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button type="button" onClick={onBack} className="min-h-11 rounded-lg border px-3 text-sm">Back</button>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold">{claim.doc_type === 'invoice' ? 'Invoice' : 'Claim'} #{sequence}</h1>
            <p className="truncate text-xs text-slate-500">{claim.job.job_number} · {claim.job.name} · {claim.period}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => void controller.save()} disabled={!controller.hasUnsavedChanges || controller.saveState === 'saving'} aria-live="polite" className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-xs disabled:opacity-70"><Save className="h-4 w-4" />{SAVE_LABELS[controller.saveState]}</button>
          {hasMoneyDocumentAction(claim, 'download_pdf') && <button type="button" onClick={() => onDownload('pdf')} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><Download className="h-4 w-4" />PDF</button>}
          {hasMoneyDocumentAction(claim, 'download_docx') && <button type="button" onClick={() => onDownload('docx')} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><FileText className="h-4 w-4" />DOCX</button>}
          {hasMoneyDocumentAction(claim, 'download_xlsx') && <button type="button" onClick={() => onDownload('xlsx')} className="inline-flex min-h-11 items-center gap-1 rounded-lg border px-3 text-sm"><FileText className="h-4 w-4" />XLSX</button>}
          {(hasMoneyDocumentAction(claim, 'send') || hasMoneyDocumentAction(claim, 'resend')) && <button type="button" onClick={onSendClient} className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-amber-500 px-3 text-sm font-medium text-white"><Send className="h-4 w-4" />Send to client</button>}
          {claim.can_submit && <button type="button" onClick={() => controller.runAction('submit')} disabled={controller.hasUnsavedChanges || controller.validation.length > 0 || controller.actionPending} className="min-h-11 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white disabled:opacity-50">Submit</button>}
          {claim.can_approve && <button type="button" onClick={() => controller.runAction('approve')} disabled={controller.actionPending} className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-sm font-medium text-white"><CheckCircle2 className="h-4 w-4" />Approve</button>}
          {claim.status === 'approved' && controller.canSendXero && <button type="button" onClick={() => controller.runAction('send_to_xero')} disabled={controller.actionPending} className="min-h-11 rounded-lg bg-purple-600 px-3 text-sm font-medium text-white">Send to Xero</button>}
        </div>
      </div>
    </header>
  )
}
